import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, GraduationCap, Gamepad2, Palette, User, Lock, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChildStore } from '../../store';
import { isDirectedSequenceMode } from '../../engine/parent-curriculum-feed';
import { useParentCurriculumStore } from '../../store/parentCurriculumStore';

interface NavItem {
  to: string;
  icon: LucideIcon;
  labelKey: string;
  match?: string[];
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: LayoutDashboard, labelKey: 'nav.today' },
  {
    to: '/learn',
    icon: GraduationCap,
    labelKey: 'nav.learn',
    match: ['/courses', '/learn', '/explore', '/field-trips', '/maps'],
  },
  { to: '/play', icon: Gamepad2, labelKey: 'nav.play', match: ['/games', '/play'] },
  { to: '/create', icon: Palette, labelKey: 'nav.create', match: ['/art', '/music', '/cooking', '/create', '/design'] },
  { to: '/me', icon: User, labelKey: 'nav.me', match: ['/me', '/portfolio', '/progress', '/settings', '/skills', '/shop'] },
];

export function BottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { children, activeChildId } = useChildStore();
  const child = children.find((candidate) => candidate.id === activeChildId);
  const _settingsPatch = useParentCurriculumStore((s) =>
    child ? s.settingsByChild[child.id] : undefined,
  );
  const directedMode = child ? isDirectedSequenceMode(child.id) : false;

  // Same-path nav clicks ("Learn" while already inside /learn/...lesson) are
  // a no-op for react-router by default — the URL doesn't change so pages
  // don't re-mount, and any inner state (LearnHubPage's tab, CoursesPage's
  // screen) stays put. Stamp a fresh `navResetAt` on the history entry so
  // subscribers can recognize the click and reset themselves.
  const handleSamePathClick = (to: string) => (e: React.MouseEvent) => {
    if (location.pathname === to) {
      e.preventDefault();
      navigate(to, { replace: true, state: { navResetAt: Date.now() } });
    }
  };

  return (
    <nav
      role="navigation"
      aria-label={t('nav.mainNavigation')}
      className="fixed bottom-0 left-0 right-0 backdrop-blur-sm border-t z-30 pb-safe"
      style={{
        backgroundColor: 'var(--color-theme-nav-bg)',
        borderColor: 'var(--color-theme-topbar-border)',
      }}
    >
      <div className="max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto flex justify-around items-center px-2 py-2">
        {directedMode ? (
          <div className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-indigo-50 px-4 text-xs font-bold text-indigo-700">
            <Lock className="h-4 w-4" />
            Parent Directed Sequence
          </div>
        ) : NAV_ITEMS.map(({ to, icon: Icon, labelKey, match }) => {
          const label = t(labelKey);
          return (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={handleSamePathClick(to)}
              // P7 a11y: every nav target is at least 44×44px (WCAG 2.5.5).
              className="flex flex-col items-center justify-center gap-0.5 rounded-lg transition-colors min-h-[44px] h-11 w-11 shrink-0"
              aria-label={label}
              style={({ isActive }) => {
                const active = isActive || !!match?.some(prefix =>
                  prefix === '/' ? location.pathname === '/' : location.pathname.startsWith(prefix),
                );
                return active
                  ? {
                      color: 'var(--color-theme-primary)',
                      backgroundColor: 'var(--color-theme-primary-light)',
                    }
                  : {
                      color: 'var(--color-theme-text-secondary)',
                    };
              }}
            >
              <Icon className="w-5 h-5" aria-hidden="true" />
              <span className="text-[10px] font-medium leading-none">{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
